import { Link, useNavigate } from 'react-router-dom'
import { CheckoutStepper } from '../components/CheckoutStepper'
import { CART_UI } from '../constants/cart'
import { EXPRESS_SHIPMENT_MODE, EXPRESS_UI } from '../constants/express'
import { CHECKOUT_STEP, ORDER_PATHS } from '../constants/orders'
import { cartLineWasTotal } from '../helpers/cart'
import { cx, formatCurrency } from '../helpers/format'
import { useCart } from '../hooks/useCart'
import { useCartShipments } from '../hooks/useCartShipments'
import '../styles/cart-page.scss'

export function CartPage() {
  const navigate = useNavigate()
  const { lines, itemCount, subtotal, compareAtTotal, savings, setQuantity, removeItem } =
    useCart()
  const { shipments } = useCartShipments()

  return (
    <div className={cx('cart-page', lines.length > 0 && 'cart-page--dock')}>
      <div className="container">
        <header className="cart-page__head">
          <div>
            <h1>{CART_UI.pageTitle}</h1>
            {itemCount > 0 ? (
              <p className="cart-page__count">
                {itemCount}{' '}
                {itemCount === 1 ? CART_UI.itemSingular : CART_UI.itemPlural}
              </p>
            ) : null}
          </div>
          {itemCount > 0 ? (
            <CheckoutStepper
              current={CHECKOUT_STEP.cart}
              onSelect={(step) => {
                if (step === CHECKOUT_STEP.address || step === CHECKOUT_STEP.checkout) {
                  navigate(ORDER_PATHS.checkout)
                }
              }}
            />
          ) : null}
        </header>

        {lines.length === 0 ? (
          <div className="cart-page__empty">
            <p>{CART_UI.emptyTitle}</p>
            <p className="cart-page__empty-hint">{CART_UI.emptyHint}</p>
            <Link to="/" className="cart-page__continue">
              {CART_UI.continueShopping}
            </Link>
          </div>
        ) : (
          <div className="cart-page__layout">
            <div className="cart-groups">
              {shipments.map((group) => (
                <section
                  key={group.mode}
                  className={cx(
                    'cart-group',
                    group.mode === EXPRESS_SHIPMENT_MODE.express && 'cart-group--express',
                  )}
                >
                  <header className="cart-group__banner">
                    <h2>{group.title}</h2>
                    {group.mode === EXPRESS_SHIPMENT_MODE.express ? (
                      <span className="cart-group__badge">{EXPRESS_UI.sameDayBadge}</span>
                    ) : null}
                  </header>
                  <ul className="cart-lines">
                    {group.lines.map((line) => {
                      const wasTotal = cartLineWasTotal(line)
                      return (
                        <li key={line.id} className="cart-line">
                          <Link to={`/product/${line.productSlug}`} className="cart-line__media">
                            {line.imageUrl ? (
                              <img src={line.imageUrl} alt="" />
                            ) : (
                              <span className="cart-line__placeholder" aria-hidden />
                            )}
                          </Link>

                          <div className="cart-line__body">
                            <Link to={`/product/${line.productSlug}`} className="cart-line__name">
                              {line.name}
                            </Link>
                            <p className="cart-line__meta">{line.variantLabel}</p>
                            {line.wrapperLabel ? (
                              <p className="cart-line__meta">{line.wrapperLabel}</p>
                            ) : null}
                            {line.addonLabels.length > 0 ? (
                              <p className="cart-line__meta">{line.addonLabels.join(' · ')}</p>
                            ) : null}
                            <p className="cart-price">
                              {wasTotal != null ? (
                                <span className="cart-price__was">{formatCurrency(wasTotal)}</span>
                              ) : null}
                              <strong>{formatCurrency(line.unitPrice * line.quantity)}</strong>
                            </p>
                          </div>

                          <div className="cart-line__actions">
                            <label className="cart-line__qty">
                              <span className="sr-only">{CART_UI.quantityLabel}</span>
                              <button
                                type="button"
                                aria-label="Decrease quantity"
                                onClick={() => setQuantity(line.id, line.quantity - 1)}
                              >
                                −
                              </button>
                              <span aria-live="polite">{line.quantity}</span>
                              <button
                                type="button"
                                aria-label="Increase quantity"
                                onClick={() => setQuantity(line.id, line.quantity + 1)}
                              >
                                +
                              </button>
                            </label>
                            <button
                              type="button"
                              className="cart-line__remove"
                              onClick={() => removeItem(line.id)}
                            >
                              {CART_UI.remove}
                            </button>
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              ))}
            </div>

            <div className="cart-side">
              <aside className={cx('cart-bill', savings > 0 && 'cart-bill--saved')}>
                {savings > 0 ? (
                  <p className="cart-bill__saved">
                    {CART_UI.savedBanner} {formatCurrency(savings)}
                  </p>
                ) : null}
                <header className="cart-bill__head">
                  <h2>{CART_UI.billTitle}</h2>
                  <span className="cart-bill__count">
                    {itemCount} {itemCount === 1 ? CART_UI.itemSingular : CART_UI.itemPlural}
                  </span>
                </header>
                <div className="cart-bill__row">
                  <span>{CART_UI.orderTotalLabel}</span>
                  <p className="cart-price">
                    {compareAtTotal != null ? (
                      <span className="cart-price__was">{formatCurrency(compareAtTotal)}</span>
                    ) : null}
                    <strong>{formatCurrency(subtotal)}</strong>
                  </p>
                </div>
                <div className="cart-bill__row cart-bill__row--grand">
                  <span>{CART_UI.grandTotalLabel}</span>
                  <strong>{formatCurrency(subtotal)}</strong>
                </div>
                <Link to={ORDER_PATHS.checkout} className="cart-page__continue">
                  {CART_UI.checkout}
                </Link>
              </aside>
              <p className="cart-side__note">{CART_UI.couponNote}</p>
            </div>
          </div>
        )}
        {lines.length > 0 ? (
          <div className="cart-dock">
            <div className="cart-dock__total">
              <span>{CART_UI.grandTotalLabel}</span>
              <p className="cart-price">
                {compareAtTotal != null ? (
                  <span className="cart-price__was">{formatCurrency(compareAtTotal)}</span>
                ) : null}
                <strong>{formatCurrency(subtotal)}</strong>
              </p>
              {savings > 0 ? (
                <em className="cart-dock__save">
                  {CART_UI.savedLabel} {formatCurrency(savings)}
                </em>
              ) : null}
            </div>
            <Link to={ORDER_PATHS.checkout} className="cart-dock__action">
              {CART_UI.checkout}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  )
}
